import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from './PageHeader';
import { apiClient } from '../services/api';
import { useLanguage, useTranslation } from '../i18n';
import type { SupportTicket } from '../types';
import './SupportPage.css';

export const SupportPage: React.FC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = useTranslation();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ subject?: string; message?: string }>({});
  const [openTickets, setOpenTickets] = useState<SupportTicket[]>([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);

  useEffect(() => {
    const fetchOpenTickets = async () => {
      try {
        const tickets = await apiClient.getSupportTickets();
        // Filter only open tickets
        setOpenTickets(tickets.filter(t => t.status === 'open'));
      } catch (error) {
        console.error('Error fetching tickets:', error);
      } finally {
        setTicketsLoading(false);
      }
    };

    fetchOpenTickets();
  }, []);

  const validateForm = (): boolean => {
    const newErrors: { subject?: string; message?: string } = {};

    if (!subject.trim()) {
      newErrors.subject = t.supportPage.enterSubject;
    } else if (subject.length > 255) {
      newErrors.subject = t.supportPage.subjectTooLong;
    }

    if (!message.trim()) {
      newErrors.message = t.supportPage.enterMessage;
    } else if (message.length > 5000) {
      newErrors.message = t.supportPage.messageTooLong;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const ticket = await apiClient.createSupportTicket({
        subject: subject.trim(),
        message: message.trim(),
      });
      
      // Navigate to the created ticket
      navigate(`/profile/support/tickets/${ticket.id}`, { replace: true });
    } catch (error) {
      console.error('Error creating ticket:', error);
      setErrors({ message: t.supportPage.createError });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getLocale = () => {
    switch (language) {
      case 'ru': return 'ru-RU';
      case 'es': return 'es-ES';
      default: return 'en-US';
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString(getLocale(), { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return t.common.yesterday;
    } else if (diffDays < 7) {
      return `${diffDays} ${t.common.daysAgo}`;
    } else {
      return date.toLocaleDateString(getLocale(), { day: 'numeric', month: 'short' });
    }
  };

  return (
    <div className="page-container support-page">
      <PageHeader title={t.supportPage.title} />

      <div className="support-content">
        {/* New ticket form */}
        <div className="new-ticket-section">
          <h2 className="section-title">{t.supportPage.newTicket}</h2>
          <form className="support-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label" htmlFor="subject">
                {t.supportPage.subject}
              </label>
              <input
                id="subject"
                type="text"
                className="form-input"
                placeholder={t.supportPage.subjectPlaceholder}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={isSubmitting}
                maxLength={255}
              />
              {errors.subject && <span className="form-error">{errors.subject}</span>}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="message">
                {t.supportPage.problemDescription}
              </label>
              <textarea
                id="message"
                className="form-textarea"
                placeholder={t.supportPage.descriptionPlaceholder}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={isSubmitting}
                maxLength={5000}
              />
              {errors.message && <span className="form-error">{errors.message}</span>}
            </div>

            <button
              type="submit"
              className="support-submit-btn"
              disabled={isSubmitting || !subject.trim() || !message.trim()}
            >
              {isSubmitting ? t.supportPage.submitting : t.supportPage.submitTicket}
            </button>
          </form>
        </div>

        {/* Open tickets list */}
        {!ticketsLoading && openTickets.length > 0 && (
          <div className="open-tickets-section">
            <h2 className="section-title">{t.supportPage.openTickets}</h2>
            <div className="tickets-list">
              {openTickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className="ticket-card"
                  onClick={() => navigate(`/profile/support/tickets/${ticket.id}`)}
                >
                  <div className="ticket-header">
                    <h3 className="ticket-subject">{ticket.subject}</h3>
                    <span className="ticket-status open">{t.supportPage.ticketOpen}</span>
                  </div>
                  
                  {ticket.last_message && (
                    <p className="ticket-preview">
                      <span className={`sender ${ticket.last_message.is_admin ? 'admin' : ''}`}>
                        {ticket.last_message.is_admin ? t.supportPage.supportTeam : t.supportPage.you}
                      </span>
                      {ticket.last_message.message}
                    </p>
                  )}

                  <div className="ticket-meta">
                    <span className="ticket-date">{formatDate(ticket.updated_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Link to all tickets history */}
        <div className="support-links">
          <a href="/profile/support/tickets" className="support-link" onClick={(e) => {
            e.preventDefault();
            navigate('/profile/support/tickets');
          }}>
            <span className="support-link-icon">📋</span>
            <div className="support-link-content">
              <div className="support-link-title">{t.supportPage.ticketsHistory}</div>
              <div className="support-link-subtitle">{t.supportPage.allTickets}</div>
            </div>
            <span className="support-link-arrow">›</span>
          </a>
        </div>
      </div>
    </div>
  );
};
